import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteSettingsContext = createContext(null);

const FALLBACK = {
    company_name: "Omega IT Ltd",
    contact_email: "",
    contact_phone: "",
    whatsapp_number: "",
    business_address: "",
    instagram_url: "",
    facebook_url: "",
    linkedin_url: "",
    whatsapp_default_message: "",
};

// Strip non-digits; treat numbers with too few non-zero digits (placeholder
// patterns like "+44 0000 000000") as missing. A real number should have
// at least 6 non-zero digits in total.
function cleanWhatsAppNumber(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (!digits) return "";
    const nonZeroCount = (digits.match(/[1-9]/g) || []).length;
    if (nonZeroCount < 6) return "";
    return digits;
}

export function buildWhatsAppLink(number, message) {
    const cleaned = cleanWhatsAppNumber(number);
    if (!cleaned) return null;
    const params = message ? `?text=${encodeURIComponent(message)}` : "";
    return `https://wa.me/${cleaned}${params}`;
}

export function SiteSettingsProvider({ children }) {
    const [settings, setSettings] = useState(FALLBACK);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API_BASE}/site-settings`)
            .then((res) => {
                if (!cancelled) setSettings({ ...FALLBACK, ...res.data });
            })
            .catch(() => {
                if (!cancelled) setSettings(FALLBACK);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const whatsAppNumberClean = cleanWhatsAppNumber(settings.whatsapp_number);
    const hasWhatsApp = !!whatsAppNumberClean;

    return (
        <SiteSettingsContext.Provider
            value={{ settings, loading, hasWhatsApp, whatsAppNumberClean }}
        >
            {children}
        </SiteSettingsContext.Provider>
    );
}

export function useSiteSettings() {
    const ctx = useContext(SiteSettingsContext);
    if (!ctx) throw new Error("useSiteSettings must be inside SiteSettingsProvider");
    return ctx;
}
